import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {buildQueryParams} from '@/utils/commons';
import {GetFormattedProductData} from '@/models/ProductListModel';
import {ProductListColConfig} from '@/services/MockData/Products/ProductListColConfig';
import {
  getProductCsvFile,
  getProductList
} from '@/services/ProductsService/product';
import {useQueries, keepPreviousData} from '@tanstack/react-query';
import {useCallback, useMemo, useRef, useState} from 'react';
import {useAppSelector} from '@/store';
import {logger} from '@/helpers/logger';

import {TAG_COLOR} from '@/utils/constants/colors';
export const useProductList = (options = {}) => {
  // Extract productIds from salesTarget state
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const salesTargetData = useAppSelector((state) => state.salesTarget);
  const Enum_PRODUCT_ID_KEYS = {
    INCLUDED_PRODUCT_IDS: 'includedProductIds',
    EXCLUDED_PRODUCT_IDS: 'excludedProductIds'
  };
  const getIdsFromRuleSet = (ruleSet, key) => {
    return ruleSet?.rules?.[0]?.[key] || [];
  };
  const {isReviewPage = false} = options;
  const extractProductIds = () => {
    if (!salesTargetData) {
      return [];
    }
    let includedIds = [];
    let excludedIds = [];
    if (isReviewPage) {
      if (!salesTargetData) return [];
      if (!salesTargetData?.ruleSet?.rules?.[0]) {
        return [];
      }
      if (salesTargetData.tabWiseData?.activeTab === 'child') {
        includedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.child?.ruleSet,
          Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS
        );
        excludedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.child?.ruleSet,
          Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS
        );
      } else {
        includedIds = getIdsFromRuleSet(
          salesTargetData.ruleSet,
          Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS
        );
        excludedIds = getIdsFromRuleSet(
          salesTargetData.ruleSet,
          Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS
        );
      }
    } else {
      if (!salesTargetData.tabWiseData) {
        return [];
      }
      if (salesTargetData.tabWiseData?.activeTab === 'child') {
        includedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.child?.ruleSet,
          Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS
        );
        excludedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.child?.ruleSet,
          Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS
        );
      } else {
        includedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.parent?.ruleSet,
          Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS
        );
        excludedIds = getIdsFromRuleSet(
          salesTargetData.tabWiseData?.parent?.ruleSet,
          Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS
        );
      }
    }
    return [...includedIds, ...excludedIds];
  };
  const productIds = extractProductIds();
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable();

  // Build productIdFilter if productIds are present
  const productIdFilter = useMemo(() => {
    if (productIds && productIds.length > 0) {
      return {
        field: 'productId',
        type: 'string',
        value: productIds.join('|')
      };
    }
    return null;
  }, [productIds]);

  // Only apply productIdFilter if the user has explicitly filtered by productId
  // Otherwise, fetch all products and handle selection in the table UI
  const mergedSearchArr = useMemo(() => {
    if (productIdFilter && isReviewPage) {
      // Remove any previous productId filter to avoid duplicates
      const filtered = searchArr.filter((f) => f.field !== 'productId');
      return [...filtered, productIdFilter];
    }

    return searchArr;
  }, [searchArr, productIdFilter, isReviewPage]);

  const queryParams = useMemo(() => {
    let pageSizeValue = pageSize;
    let pageValue = pageIndex || 0;

    if (options?.selectAll) {
      pageSizeValue = Math.max(1000, totalRecords || 1000);
    } else if (isReviewPage && productIds?.length > 0) {
      pageSizeValue = Math.max(1, productIds.length || 1);
    }

    return buildQueryParams({
      ...(sortingObj?.column &&
        sortingObj.column !== 'includeExclude' && {
          sortBy: sortingObj.column,
          order: sortingObj.direction
        }),
      pageSize: pageSizeValue,
      page: pageValue
    });
  }, [
    sortingObj?.column,
    sortingObj?.direction,
    pageIndex,
    pageSize,
    options?.selectAll,
    totalRecords,
    isReviewPage,
    productIds.length
  ]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['PRODUCT_LIST', queryParams, mergedSearchArr],
        queryFn: async () => {
          const response = await getProductList(queryParams, mergedSearchArr);

          if (response?.totalRecords) {
            setTotalRecords(response.totalRecords);
          }

          return response;
        },
        placeholderData: keepPreviousData
      }
    ]
  });

  const onDownloadBtnClick = async (selectedIds) => {
    const body = {
      productIds: selectedIds,
      filters: mergedSearchArr
    };

    const data = await getProductCsvFile(body, queryParams);
    const fileName = `Product_${getDateWithTimeString()}`;
    downloadCSVFiles(data, fileName);
  };
  const fetchAllProducts = async (searchArr = []) => {
    try {
      const total = totalRecords;

      const queryParams = `page=0&pageSize=${total}`;

      const response = await getProductList(queryParams, searchArr);

      if (!response.data) {
        return [];
      }

      return response.data;
    } catch (error) {
      logger.error('[useProductList][fetchAllProducts] Error:', error);
      throw error;
    }
  };

  const onRowsPerPageChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPageIndex(0);
    },
    [setPageSize, setPageIndex]
  );

  // Mirror useSalesTargetsQuery: rapid page/filter changes can break the
  // keepPreviousData chain, leaving `data` briefly undefined mid-fetch.
  // Serve the last good payload during that gap so totalRecords never
  // becomes undefined (which resets AdvancedPagination totalPages to 1,
  // causing it to clamp the displayed page back to 1).
  const lastGoodDataRef = useRef(null);
  const productListData = useMemo(() => {
    const formatted = GetFormattedProductData(data);
    const hasRows = formatted?.data?.length > 0;
    if (hasRows || !isFetching) {
      lastGoodDataRef.current = formatted;
      return formatted;
    }
    return lastGoodDataRef.current ?? formatted;
  }, [data, isFetching]);
  const getTagProps = (included) => {
    if (included === undefined) {
      return {text: '—', color: null}; // Default case for undefined
    }
    return {
      text: included ? 'Exclude' : 'Include',
      color: included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150
    };
  };
  return {
    productListData,
    productListColConfigData: ProductListColConfig,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    pageIndex,
    pageSize,
    setPageSize,
    onRowsPerPageChange,
    // Only show skeleton (isLoading=true) when there is genuinely no prior
    // data — i.e. on the very first load. On subsequent page/filter changes
    // keepPreviousData may briefly set isLoading=true if the cache misses;
    // guard against that by checking whether we have a last-known payload.
    // This matches useSalesTargetsQuery which returns isLoading directly and
    // prevents DataTable from rendering data=[] during page transitions
    // (which would reset totalPages→1 and clamp the pagination display).
    isLoading: isLoading && !lastGoodDataRef.current,
    isFetching,
    fetchAllProducts,
    totalRecords,
    handleResetAll,
    getTagProps,
    initialSortConfig: sortingObj,
    searchArr
  };
};
